import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { renderAsync } from 'docx-preview';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Download,
  FileImage,
  FileText,
  Info,
  Loader2,
  MinusCircle,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  UserCheck,
  Users,
  XCircle,
} from 'lucide-react';
import { WORKFLOW_STAGE, getWorkflowState, getApproval } from '@/lib/workflowHelpers';
import {
  agentContextLabel,
  buildRoleModHaloSummary,
  evidenceDisplayLabel,
  formatWhen,
  getRoleModFailureContext,
  getRoleModGateRecords,
  getRoleModOriginalRequest,
  getRoleModApprovalTransport,
  getRoleModClosure,
  getRoleModDocumentGroups,
  roleModEvidenceKind,
  getRoleModRoleAnalysis,
  getRoleModSafetyChecks,
  describeAuthorizationChange,
  getRoleModV4View,
  humanizeRoleModError,
  isRoleModV4,
  roleModV4ReasonText,
} from '@/lib/roleModificationView';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { readBlobBuffer, readBlobText, readSpreadsheetRows } from '@/lib/spreadsheetPreview';
import { permissionDeniedTitle } from '@/lib/permissionUi';

/** Label, optional tooltip hint, and the value. An absent value is only "Not provided";
 * the hint never stands in for content. */
export function Fact({ label, hint, value, mono = false, testId }) {
  return (
    <div className="rounded-lg border border-cp-line/70 bg-background/30 p-3" data-testid={testId}>
      <p className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground" title={hint || undefined}>
        {label}
        {hint ? <Info className="h-3 w-3 shrink-0" aria-label={hint} /> : null}
      </p>
      <div className={`mt-1 text-sm break-words ${mono ? 'font-mono' : ''}`}>
        {value || <span className="text-muted-foreground">Not provided</span>}
      </div>
    </div>
  );
}

/** Absolute URL of a stored evidence file. The path is relative to the incidents API. */
export function evidenceFileUrl(doc) {
  if (!doc?.path) return null;
  const base = String(INCIDENTS_API || '').replace(/\/$/, '');
  return `${base}${doc.path}`;
}

function saveEvidenceFile(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename || 'evidence';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Draws a stored file in place: image, PDF page, Word document, or text.
 * Used for both the small tile in Documents and the full-size dialog.
 */
function EvidenceContent({ doc, enabled = true, compact = false, onBlob }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [text, setText] = useState('');
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const url = evidenceFileUrl(doc);
  // Filename and MIME decide the preview. A stored trace spreadsheet uses the
  // same worksheet view as an SU53 spreadsheet, even if an older kind was saved.
  const detected = roleModEvidenceKind(doc?.contentType || doc?.content_type, doc?.filename);
  const kind = detected !== 'file' ? detected : (doc?.kind || 'file');
  // Images go through the same authenticated request as documents. A plain
  // <img src> does not send the login token, so the picture stays broken.
  const needsFetch = kind === 'image' || kind === 'pdf' || kind === 'docx' || kind === 'text' || kind === 'sheet';

  useEffect(() => {
    if (!enabled || !url || !needsFetch) return undefined;
    let cancelled = false;
    let created = null;
    const load = async () => {
      setLoading(true);
      setLoaded(false);
      setError(null);
      setText('');
      setRows([]);
      try {
        const { data } = await axios.get(url, { responseType: 'blob' });
        if (cancelled) return;
        if (onBlob) onBlob(data);
        if (kind === 'sheet') {
          try {
            setRows(readSpreadsheetRows(await readBlobBuffer(data)));
          } catch (err) {
            if (!cancelled) setError('This spreadsheet could not be drawn in the page. Download it to open it.');
            return;
          }
        } else if (kind === 'image' || kind === 'pdf') {
          const blobType = String(data?.type || '');
          const file =
            kind === 'image' && (!blobType || blobType === 'application/octet-stream')
              ? new Blob([data], { type: doc.contentType || 'image/png' })
              : data;
          created = URL.createObjectURL(file);
          setObjectUrl(created);
        } else if (kind === 'text') {
          setText(await readBlobText(data));
        } else if (kind === 'docx' && containerRef.current) {
          containerRef.current.innerHTML = '';
          await renderAsync(await readBlobBuffer(data), containerRef.current, null, {
            className: 'docx-preview-surface',
            inWrapper: !compact,
            ignoreWidth: compact,
            ignoreHeight: compact,
          });
        }
      } catch (err) {
        if (!cancelled) setError('The document could not be opened.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoaded(true);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
      setObjectUrl(null);
    };
  }, [enabled, url, needsFetch, kind, compact, onBlob, doc?.contentType]);

  const frame = compact ? 'h-48 w-full' : 'h-[70vh] w-full';
  return (
    <>
      {loading ? (
        <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Opening document
        </div>
      ) : null}
      {error ? <p className="p-3 text-sm text-cp-crit">{error}</p> : null}
      {kind === 'image' && objectUrl ? (
        <img
          src={objectUrl}
          alt={doc.filename}
          className={compact ? 'max-h-48 w-full object-contain object-left' : 'max-h-[70vh] w-full object-contain'}
        />
      ) : null}
      {kind === 'pdf' && objectUrl ? (
        <iframe
          title={doc.filename}
          src={compact ? `${objectUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH` : objectUrl}
          className={`${frame} ${compact ? 'pointer-events-none' : ''}`}
        />
      ) : null}
      {kind === 'docx' ? (
        <div
          ref={containerRef}
          className={`${compact ? 'h-48 overflow-hidden p-3 text-[10px] leading-snug' : 'max-h-[70vh] overflow-auto'} bg-white text-black`}
        />
      ) : null}
      {kind === 'text' && text ? (
        <pre className={`${compact ? 'h-48 overflow-hidden' : 'max-h-[70vh] overflow-auto'} whitespace-pre-wrap p-3 text-sm`} data-testid="role-mod-text-preview">
          {text}
        </pre>
      ) : null}
      {kind === 'sheet' && rows.length ? (
        <div className={`${compact ? 'h-48' : 'max-h-[70vh]'} overflow-auto`} data-testid="role-mod-sheet-preview">
          <table className="min-w-full border-collapse text-left text-xs">
            <tbody>
              {rows.slice(0, 200).map((row, index) => (
                <tr key={index} className="border-b border-cp-line/40">
                  {row.slice(0, 40).map((cell, cellIndex) => (
                    <td key={cellIndex} className="whitespace-nowrap px-2 py-1 align-top">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {kind === 'sheet' && loaded && !loading && !error && rows.length === 0 ? (
        <div className="flex h-40 flex-col items-center justify-center gap-2 p-4 text-center text-sm text-muted-foreground">
          <FileText className="h-8 w-8 text-cp-indigo" />
          <span className="font-mono text-xs">{doc.filename}</span>
          <span>This spreadsheet has no visible rows. Download it to open it.</span>
        </div>
      ) : null}
      {kind === 'doc' || kind === 'file' ? (
        <div className={`flex ${compact ? 'h-48' : 'h-40'} flex-col items-center justify-center gap-2 p-4 text-center text-sm text-muted-foreground`}>
          <FileText className="h-8 w-8 text-cp-indigo" />
          <span className="font-mono text-xs">{doc.filename}</span>
          <span>This file type cannot be drawn in the page. Download it to open it.</span>
        </div>
      ) : null}
    </>
  );
}

/** Clickable tile under each document, the same for screenshots and documents. */
function EvidenceThumbnail({ doc, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="mt-3 block w-full max-w-md overflow-hidden rounded-md border bg-background/40 text-left hover:border-cp-indigo"
      title={`View ${doc.filename}`}
      data-testid="role-mod-document-thumbnail"
    >
      <EvidenceContent doc={doc} compact />
    </button>
  );
}

/** In-ticket preview. Download is a separate action so viewing does not save the file. */
function EvidencePreviewDialog({ doc, open, onOpenChange }) {
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState(null);
  const url = evidenceFileUrl(doc);

  const download = async () => {
    try {
      const file = blob || (await axios.get(url, { responseType: 'blob' })).data;
      saveEvidenceFile(file, doc.filename);
    } catch (err) {
      setError('The document could not be downloaded.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{doc.filename}</DialogTitle>
          <DialogDescription>{doc.step || 'Uploaded evidence'}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end">
          <Button type="button" size="sm" variant="outline" onClick={download} data-testid="role-mod-download-evidence">
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
        </div>
        {error ? <p className="text-sm text-cp-crit">{error}</p> : null}
        <div className="overflow-hidden rounded-md border">
          <EvidenceContent doc={doc} enabled={open} onBlob={setBlob} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Filename plus an in-ticket viewer. Download is separate from viewing. */
function EvidenceFile({ doc, sourceLabel, thumbnail = false }) {
  const [open, setOpen] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  if (!doc?.filename && !sourceLabel) {
    return <span className="text-muted-foreground">Not provided</span>;
  }
  const Icon = doc?.kind === 'image' ? FileImage : FileText;
  const url = doc?.viewable ? evidenceFileUrl(doc) : null;
  // Screenshots stay as a small picture. Word and PDF stay as View / Download;
  // a shrunk page of text is not readable in the list.
  const showPicture = thumbnail && url && doc?.kind === 'image';
  const download = async () => {
    try {
      setDownloadError(null);
      const { data } = await axios.get(url, { responseType: 'blob' });
      saveEvidenceFile(data, doc.filename);
    } catch (err) {
      setDownloadError('The document could not be downloaded.');
    }
  };
  return (
    <span className={showPicture ? 'block' : 'inline-flex flex-wrap items-center gap-2'}>
      <span className="inline-flex flex-wrap items-center gap-2">
      <Icon className="h-4 w-4 text-cp-indigo" />
      {doc?.filename ? <span className="font-mono">{doc.filename}</span> : null}
      {sourceLabel ? (
        <Badge variant="outline" className="text-[10px]">
          {sourceLabel}
        </Badge>
      ) : null}
      {url ? (
        <>
          <button
            type="button"
            className="text-sm font-medium text-cp-indigo hover:underline"
            onClick={() => setOpen(true)}
          >
            View
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-medium text-cp-indigo hover:underline"
            onClick={download}
            data-testid="role-mod-download-file"
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </button>
          <EvidencePreviewDialog doc={doc} open={open} onOpenChange={setOpen} />
        </>
      ) : doc?.filename ? (
        <span className="text-xs text-muted-foreground">File was not kept on this ticket</span>
      ) : null}
      {downloadError ? <span className="text-xs text-cp-crit">{downloadError}</span> : null}
      </span>
      {showPicture ? <EvidenceThumbnail doc={doc} onOpen={() => setOpen(true)} /> : null}
    </span>
  );
}

function EvidenceChip({ artifact, sourceLabel, step }) {
  if (!artifact?.filename && !sourceLabel) {
    return <span className="text-muted-foreground">Not provided</span>;
  }
  const contentType = String(artifact?.content_type || '').toLowerCase();
  const path = artifact?.document?.path || null;
  return (
    <EvidenceFile
      sourceLabel={sourceLabel}
      doc={{
        step,
        filename: artifact?.filename,
        contentType,
        path,
        viewable: Boolean(path),
        kind: roleModEvidenceKind(contentType, artifact?.filename),
      }}
    />
  );
}

export function Disclosure({ label, openLabel, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);
  return (
    <div data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-sm font-medium text-cp-indigo hover:underline"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {open ? openLabel || label : label}
      </button>
      {open ? <div className="mt-3">{children}</div> : null}
    </div>
  );
}

/** Human-readable, stage-aware narrative generated from persisted state. */
export function RoleModHaloSummary({ ticket }) {
  const paragraphs = buildRoleModHaloSummary(ticket);
  if (!paragraphs.length) return null;
  return (
    <Card className="border-cp-indigo/40 bg-cp-inset" data-testid="role-mod-halo-summary">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-cp-indigo" />
          HALO Summary
        </CardTitle>
        <CardDescription>What happened, what HALO proposes, and what is needed next.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm leading-relaxed">
        {paragraphs.map((text, idx) => (
          <p key={idx}>{text}</p>
        ))}
      </CardContent>
    </Card>
  );
}

/** Current → Proposed addition → Result, plus the failed action vs evidence split. */
export function RoleModChangeCenterpiece({ ticket, decision }) {
  const stage = ticket?.workflow_stage;
  const state = getWorkflowState(ticket);
  const failure = getRoleModFailureContext(ticket);
  const applied =
    state.mcp_result?.role_change_applied === true ||
    String(state.transport_status || '').toUpperCase() === 'APPLIED';
  const hasProposal = Boolean(decision?.proposedAddition);
  const title = applied ? 'Applied role change' : hasProposal ? 'Proposed role change' : 'Authorization failure';
  return (
    <Card data-testid="role-mod-change-centerpiece">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>
          {hasProposal
            ? `Exact content change to existing role ${decision.targetRole || ''}. Role assignments are not changed.`
            : 'What failed and how HALO knows.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasProposal ? (
          <div className="grid gap-3 md:grid-cols-3" data-testid="role-mod-before-after">
            <div className="rounded-lg border bg-background/40 p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Current</p>
              <p className="mt-1 font-mono text-sm">{decision.currentAuthorization || 'Not available'}</p>
            </div>
            <div className="rounded-lg border border-cp-indigo/50 bg-background/40 p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {applied ? 'Applied addition' : 'Proposed addition'}
              </p>
              <p className="mt-1 font-mono text-sm font-semibold text-cp-indigo">
                {decision.proposedAddition || 'Not available'}
              </p>
            </div>
            <div className="rounded-lg border border-cp-ok/50 bg-background/40 p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {applied ? 'Result' : 'Result after approval'}
              </p>
              <p className="mt-1 font-mono text-sm font-semibold text-cp-ok">
                {decision.resultingAuthorization || 'Not available'}
              </p>
            </div>
          </div>
        ) : null}
        <div className="grid gap-3 md:grid-cols-3">
          <Fact
            label="Failed transaction / action"
            value={failure.failedActionLabel}
            mono={Boolean(failure.failedAction)}
            testId="role-mod-failed-action"
          />
          <Fact
            label="Authorization failure"
            value={failure.authorizationFailure}
            mono
            testId="role-mod-authorization-failure"
          />
          <Fact
            label="Evidence source"
            value={
              <EvidenceChip artifact={failure.evidenceArtifact} sourceLabel={failure.evidenceSource} />
            }
            testId="role-mod-evidence-source"
          />
        </div>
        {stage === WORKFLOW_STAGE.ROLE_SELECTION_REQUIRED ? (
          <p className="text-xs text-muted-foreground">
            The proposed addition appears here once a target role is selected.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** The requester's words stay visible through the whole lifecycle. */
export function RoleModOriginalRequest({ ticket }) {
  const request = getRoleModOriginalRequest(ticket);
  return (
    <Card data-testid="role-mod-original-request">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ClipboardList className="h-5 w-5" />
          Original Request
        </CardTitle>
        {request.submittedAt ? (
          <CardDescription>Submitted {formatWhen(request.submittedAt)}</CardDescription>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Title</p>
          <p className="mt-1 font-medium">{request.title || 'Not provided'}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Request description / business reason
          </p>
          <p className="mt-1 whitespace-pre-wrap" data-testid="role-mod-request-description">
            {request.description || <span className="text-muted-foreground">Not provided</span>}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Fact label="Affected user" value={request.user} mono />
          <Fact label="Instance" value={request.instance || request.systemClient} mono />
          <Fact
            label="Evidence file"
            value={request.evidenceFilename ? <EvidenceChip artifact={{ filename: request.evidenceFilename }} /> : null}
          />
          {request.requester ? <Fact label="Requester" value={request.requester} /> : null}
          {request.manager ? <Fact label="Manager" value={request.manager} /> : null}
          {request.roleOwner ? <Fact label="Role owner" value={request.roleOwner} /> : null}
        </div>
      </CardContent>
    </Card>
  );
}

const FIT_BREAKDOWN_LABELS = {
  exact_object_present: 'exact object present',
  exact_field_present: 'exact field present',
  existing_related_values: 'existing values',
  other_fields_same_object: 'other fields on the same object',
  related_transaction_context: 'business transaction context',
};

function RoleDetailRow({ row, selectable, selectedRole, onSelectRole, scoring }) {
  const stored = Object.entries(row.scoreBreakdown || {});
  const breakdown =
    scoring?.version === 'v2' || scoring?.version === 'v3'
      ? stored.filter(([key]) => key in FIT_BREAKDOWN_LABELS)
      : stored;
  const shownScore = scoring?.version === 'v3' ? row.displayScore : row.score;
  const breakdownLabel = (key) => FIT_BREAKDOWN_LABELS[key] || key.replaceAll('_', ' ');
  return (
    <li
      className={`space-y-1 rounded-md border p-3 ${
        row.selected ? 'border-cp-indigo/60 bg-cp-inset' : 'border-cp-line/60'
      }`}
      data-testid={`role-mod-role-row-${row.role}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {selectable && row.accepted ? (
          <input
            type="radio"
            name="role-mod-target"
            value={row.role}
            checked={selectedRole === row.role}
            onChange={() => onSelectRole(row.role)}
            aria-label={`Select ${row.role}`}
          />
        ) : null}
        <span className="font-mono">{row.role}</span>
        <Badge variant={row.selected ? 'default' : 'outline'}>
          {row.selected
            ? 'Selected'
            : row.accepted
              ? selectable
                ? 'Candidate'
                : 'Considered'
              : 'Rejected'}
        </Badge>
        {shownScore != null ? (
          <span className="ml-auto text-xs text-muted-foreground">
            {scoring?.scoreLabel || 'Role Fit Score'} {shownScore}
            {scoring?.version === 'v3' ? ' / 100' : ''}
          </span>
        ) : null}
      </div>
      {row.fitWhy?.length ? (
        <ul className="list-disc pl-4 text-xs text-muted-foreground">
          {row.fitWhy.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : row.conciseReason ? (
        <p className="text-xs">{row.conciseReason}</p>
      ) : null}
      {row.currentValues.length > 0 ? (
        <p className="text-xs text-muted-foreground font-mono">
          current values: {row.currentValues.join(', ')}
        </p>
      ) : null}
      {row.assignedUserCount != null ? (
        <p className="text-xs text-muted-foreground">
          Impact: {row.assignedUserCount} assigned user{row.assignedUserCount === 1 ? '' : 's'}
          {row.shared ? ' · shared role' : ' · not shared'}
        </p>
      ) : null}
      {breakdown.length > 0 ? (
        <Disclosure label="View calculation" openLabel="Hide calculation" testId={`role-mod-score-calc-${row.role}`}>
          {scoring?.version === 'v3' ? (
            <p className="text-xs text-muted-foreground">Raw policy score: {row.score} / 92</p>
          ) : null}
          <p className="text-xs text-muted-foreground font-mono">
            {breakdown
              .map(([key, value]) => `${breakdownLabel(key)} ${value >= 0 ? '+' : ''}${value}`)
              .join(' · ')}
          </p>
        </Disclosure>
      ) : null}
    </li>
  );
}

function otherAcceptedPhrase(analysis) {
  const count = analysis.otherAccepted || 0;
  if (!count) return '';
  const contained = `${count} also contained the object`;
  const weaker = analysis.weakerAccepted || 0;
  if (weaker === count) {
    return `; ${contained} but ${count === 1 ? 'was' : 'were'} a weaker role fit`;
  }
  if (weaker > 0) {
    return `; ${contained}, ${weaker} with a weaker role fit`;
  }
  return `; ${contained}`;
}

/** Human-first role analysis: selected role + why, technical rows on demand. */
const DISPOSITION_LABEL = {
  included: 'Included',
  excluded: 'Excluded',
  needs_review: 'Needs review',
  needs_confirmation: 'Needs confirmation',
  insufficient_evidence: 'Insufficient evidence',
};

const MODE_LABEL = {
  add_to_instance: 'Add to existing authorization',
  create_instance: 'New authorization object',
};

function FieldList({ fields }) {
  if (!fields?.length) return <span className="text-muted-foreground">No visible value</span>;
  return (
    <ul className="mt-1 space-y-1 font-mono text-xs">
      {fields.map((field) => (
        <li key={field.field}>
          {field.field} = {field.values.join(', ') || 'no visible value'}
        </li>
      ))}
    </ul>
  );
}

/** Group instances under their object without merging instances or values. */
function groupAuthInstances(nodes) {
  const groups = [];
  const byObject = new Map();
  (nodes || []).forEach((node) => {
    const object = node.object || 'Object';
    let group = byObject.get(object);
    if (!group) {
      group = { object, instances: [] };
      byObject.set(object, group);
      groups.push(group);
    }
    group.instances.push(node);
  });
  return groups;
}

/**
 * Authorization object → instance → field → value.
 * Each stored instance stays on its own branch, and each value stays on its own line.
 */
function AuthTree({ nodes, prominent = false, showObject = true }) {
  const groups = groupAuthInstances(nodes);
  if (!groups.length) return null;
  return (
    <ul className="mt-2 space-y-3">
      {groups.map((group) => (
        <li key={group.object} data-testid={`role-mod-v4-auth-object-${group.object}`}>
          {showObject ? (
            <p className={`font-mono text-sm ${prominent ? 'font-semibold' : 'font-medium text-muted-foreground'}`}>{group.object}</p>
          ) : null}
          <ul className="mt-1 space-y-2 border-l border-cp-line/70 pl-3">
            {group.instances.map((instance, index) => (
              <li
                key={`${instance.auth || 'auth'}-${instance.variant || 'variant'}-${index}`}
                data-testid={`role-mod-v4-auth-${group.object}-${instance.auth || index}`}
              >
                <p className="text-xs font-medium">Authorization instance {index + 1}</p>
                <p className="font-mono text-xs">AUTH: {instance.auth || '—'}</p>
                <p className="text-[11px] text-muted-foreground">Variant: {instance.variant || '—'}</p>
                {instance.fields?.length ? (
                  <ul className="mt-1 space-y-1 pl-3">
                    {instance.fields.map((field) => (
                      <li key={field.field}>
                        <p className="font-mono text-xs">{field.field}</p>
                        <ul className="pl-3">
                          {(field.values?.length ? field.values : ['no visible value']).map((value, valueIndex) => (
                            <li key={`${field.field}-${value}-${valueIndex}`} className="font-mono text-xs">{value}</li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="pl-3 text-xs text-muted-foreground">No visible value</p>
                )}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/**
 * Compact, tree-style comparison for one assigned role. Facts only: what the
 * role has now, what each requested object would need, and how many users
 * share the role. There is no score.
 */
function RoleComparisonDetail({ role, defaultOpen = false, selected = false }) {
  const requested = role.requested || [];
  const otherNodes = role.otherAccess || [];
  // A confirmed role is never described as "Not recommended" next to its
  // Selected badge; the structural fact about the change is shown instead.
  const headline = selected && !role.recommended ? role.why : role.recommendationSummary || role.why;
  return (
    <div className="mt-2 space-y-2 text-sm" data-testid={`role-mod-v4-comparison-${role.role}`}>
      {headline ? <p>{headline}</p> : null}
      <Disclosure
        label="Show role details"
        openLabel="Hide role details"
        defaultOpen={defaultOpen}
        testId={`role-mod-v4-details-${role.role}`}
      >
        <ul className="space-y-2 border-l border-cp-line/70 pl-3">
          <li>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Existing transactions</p>
            <p className="font-mono text-xs">
              {role.transactionCodes?.length ? `T-codes: ${role.transactionCodes.join(', ')}` : 'No S_TCODE values on this role'}
            </p>
          </li>
          <li>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Affected users</p>
            <p className="text-xs">
              {role.assignmentCount != null
                ? `${role.assignmentCount} assigned user${Number(role.assignmentCount) === 1 ? '' : 's'} would receive this change`
                : 'Assigned-user count was not read'}
            </p>
          </li>
          {requested.map((item) => (
            <li key={item.checkId || item.object} data-testid={`role-mod-v4-requested-${role.role}-${item.object}`}>
              <p className="font-mono text-xs">
                {item.object}
                {' · '}
                <span className="font-sans">
                  {item.existsOnRole
                    ? `on this role — ${item.placementLabel || 'existing authorization extended'}`
                    : `not on this role — ${item.placementLabel || 'new authorization object'}`}
                </span>
              </p>
              {item.instances?.length ? (
                <AuthTree
                  prominent
                  showObject={false}
                  nodes={item.instances.map((instance) => ({ ...instance, object: item.object }))}
                />
              ) : null}
              <p className="mt-1 text-xs">
                {item.missing?.length
                  ? `Missing on ${item.object}: ${item.missing.map((field) => `${field.field} = ${field.values.join(', ')}`).join('; ')}`
                  : item.placement === 'duplicate'
                    ? `${item.object} already contains the requested values.`
                    : `Nothing missing is recorded for ${item.object}.`}
              </p>
            </li>
          ))}
          {otherNodes.length ? (
            <li>
              <Disclosure label="Other authorizations on this role" openLabel="Hide other authorizations" testId={`role-mod-v4-tree-${role.role}`}>
                <AuthTree nodes={otherNodes} />
              </Disclosure>
            </li>
          ) : null}
        </ul>
      </Disclosure>
    </div>
  );
}

function DeclineEscalation({ testId, disabled, submitting, onDecline }) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => onDecline()}
      disabled={disabled || submitting}
      data-testid={`${testId}-submit`}
    >
      Decline and escalate
    </Button>
  );
}

/** Stored V4 decision. Rendering this does not call the model or rebuild operations. */
export function RoleModV4Analysis({
  ticket,
  selectedRole: pickedRole = '',
  onSelectRole,
  onConfirmSelection,
  onConfirmEvidence,
  onDeclineEvidence,
  onDeclineRole,
  submitting = false,
  canMutate = false,
}) {
  const view = getRoleModV4View(ticket);
  const failure = getRoleModFailureContext(ticket);
  const traceEvidence = /trace/i.test(failure.evidenceSource || '');
  const [authChoices, setAuthChoices] = useState({});
  const [evidenceChoices, setEvidenceChoices] = useState({});
  if (!view) return null;
  const comparisons = view.comparisons || [];
  const reviewable = (disposition) => disposition === 'included' || disposition === 'needs_confirmation';
  const evidenceChecked = (check) => (
    Object.prototype.hasOwnProperty.call(evidenceChoices, check.id)
      ? evidenceChoices[check.id] === true
      : check.disposition === 'included'
  );
  const editableChecks = view.checks.filter((check) => reviewable(check.disposition));
  const selectedEvidenceCount = editableChecks.filter(evidenceChecked).length;
  const confirm = () => {
    if (!onConfirmSelection || !pickedRole) return;
    const choices = (view.authChoice?.checks || []).map((check) => authChoices[check.checkId]).filter(Boolean);
    onConfirmSelection({ role: pickedRole, authChoices: choices.length ? choices : undefined });
  };
  const confirmEvidence = () => {
    if (!onConfirmEvidence || selectedEvidenceCount === 0) return;
    onConfirmEvidence({
      checks: editableChecks.map((check) => ({
        check_id: check.id,
        include: evidenceChecked(check),
      })),
    });
  };
  return (
    <div className="space-y-4" data-testid="role-mod-v4">
      {view.humanReview ? (
        <Card className="border-cp-crit" data-testid="role-mod-v4-human-reason">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-cp-crit">
              <ShieldAlert className="h-5 w-5" />
              Human review
            </CardTitle>
            <CardDescription>{view.humanReview}</CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      <Card data-testid="role-mod-v4-su53">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{traceEvidence ? 'Authorization trace' : 'SU53 Analysis'}</CardTitle>
          <CardDescription>
            {view.requiresEvidenceConfirmation
              ? 'Included checks are selected and can be cleared. Needs confirmation checks can be selected. Insufficient evidence and excluded checks stay visible for audit and have no selection control.'
              : 'Every failed check stored from the evidence.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Fact
              label="Business action"
              hint="Optional plain-English activity, for example Post goods movement."
              value={view.businessAction}
              testId="role-mod-v4-business-action"
            />
            <Fact
              label="Transaction"
              hint="SAP T-code, for example MIGO."
              value={view.businessTransaction}
              mono
              testId="role-mod-v4-transaction"
            />
          </div>
          <ul className="space-y-3">
            {view.checks.map((check) => (
              <li key={check.id} className="rounded-md border border-cp-line/70 p-3" data-testid={`role-mod-v4-check-${check.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  {view.requiresEvidenceConfirmation && reviewable(check.disposition) ? (
                    <input
                      type="checkbox"
                      checked={evidenceChecked(check)}
                      onChange={(event) => setEvidenceChoices((current) => ({
                        ...current,
                        [check.id]: event.target.checked,
                      }))}
                      disabled={!canMutate || submitting}
                      aria-label={`Include ${check.object}`}
                      data-testid={`role-mod-v4-include-${check.id}`}
                    />
                  ) : !view.requiresEvidenceConfirmation && check.disposition === 'included' ? (
                    <input type="checkbox" checked readOnly disabled aria-label={`${check.object} included`} />
                  ) : null}
                  <span className="font-mono text-sm">{check.object}</span>
                  {check.application ? <span className="text-xs text-muted-foreground">{check.application}</span> : null}
                  <Badge
                    variant={check.disposition === 'included' ? 'default' : 'outline'}
                    className={check.insufficient ? 'border-cp-crit text-cp-crit' : undefined}
                  >
                    {check.informational && !check.insufficient
                      ? 'Informational — not included'
                      : DISPOSITION_LABEL[check.disposition] || check.disposition}
                  </Badge>
                </div>
                <FieldList fields={check.fields} />
                {check.missingLabel ? (
                  <p className="mt-1 text-sm font-medium text-cp-crit" data-testid={`role-mod-v4-missing-${check.id}`}>
                    {check.missingLabel}
                  </p>
                ) : null}
                {check.resultText ? <p className="mt-1 text-xs text-muted-foreground">{check.resultText}</p> : null}
                <p className="mt-2 text-sm">{check.reason}</p>
              </li>
            ))}
          </ul>
          {view.insufficientChecks.length ? (
            <p className="text-xs text-muted-foreground" data-testid="role-mod-v4-insufficient-note">
              Checks marked Insufficient evidence are kept on the ticket for audit and are not part of the change.
              {traceEvidence
                ? null
                : ' The security team can supply an authorization trace on a separate ticket. Trace values are never merged into this SU53 ticket.'}
            </p>
          ) : null}
          {view.evidenceAudit ? (
            <div className="text-sm" data-testid="role-mod-v4-evidence-audit">
              {view.evidenceAudit.recommended.length ? <p>HALO recommended: {view.evidenceAudit.recommended.join(', ')}</p> : null}
              {view.evidenceAudit.confirmed.length ? <p>Reviewer confirmed: {view.evidenceAudit.confirmed.join(', ')}</p> : null}
              {view.evidenceAudit.removed.length ? <p>Reviewer removed: {view.evidenceAudit.removed.join(', ')}</p> : null}
            </div>
          ) : null}
          {view.requiresEvidenceConfirmation && selectedEvidenceCount === 0 ? (
            <p className="text-sm text-cp-crit" data-testid="role-mod-v4-evidence-required">
              Select at least one authorization check to continue. If none of these checks should be granted, cancel or reject the remediation.
            </p>
          ) : null}
          {view.requiresEvidenceConfirmation && onConfirmEvidence ? (
            <div className="flex flex-wrap items-start gap-3">
              <Button
                type="button"
                onClick={confirmEvidence}
                disabled={!canMutate || submitting || selectedEvidenceCount === 0}
                data-testid="role-mod-v4-confirm-evidence"
              >
                {submitting ? 'Saving the evidence choice…' : 'Confirm authorization checks'}
              </Button>
              {onDeclineEvidence ? (
                <DeclineEscalation
                  testId="role-mod-v4-decline-evidence"
                  disabled={!canMutate}
                  submitting={submitting}
                  onDecline={() => onDeclineEvidence({
                    checks: editableChecks.map((check) => ({
                      check_id: check.id,
                      include: evidenceChecked(check),
                    })),
                  })}
                />
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card data-testid="role-mod-v4-roles">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            Role Analysis
          </CardTitle>
          <CardDescription>
            One assigned role receives the whole included change. HALO recommends a role from structural facts only. The reviewer confirms it; the exact change is then re-read, rebuilt, and applied.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {view.recommendedRole && !view.selectedRole ? (
            <div data-testid="role-mod-v4-suggested">
              <div className="flex flex-wrap items-center gap-2">
                <Badge>Recommended</Badge>
                <p className="font-mono font-semibold">{view.recommendedRole}</p>
              </div>
              <p className="mt-1">{view.recommendationSummary}</p>
            </div>
          ) : null}
          {view.selectedRole ? (
            <div data-testid="role-mod-v4-selected">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Selected role</p>
              <p className="font-mono font-semibold">{view.selectedRole}</p>
              <p className="mt-1">{view.whySelected}</p>
            </div>
          ) : view.requiresEvidenceConfirmation ? (
            <p>Role analysis starts after the authorization checks are confirmed.</p>
          ) : view.requiresSelection ? (
            <p>Confirm the target role. No SAP change has been built yet.</p>
          ) : (
            <p>No role was selected.</p>
          )}
          {view.reReview ? (
            <div className="flex items-start gap-2 rounded-md border border-cp-crit/60 p-3 text-cp-crit" data-testid="role-mod-v4-re-review">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{view.reReview.message}</p>
            </div>
          ) : view.selectionError ? (
            <p className="text-cp-crit">{view.selectionError}</p>
          ) : null}
          {comparisons.length ? (
            <ul className="space-y-2">
              {comparisons.map((role) => (
                <li key={role.role} className="rounded-md border border-cp-line/60 p-3" data-testid={`role-mod-v4-role-${role.role}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    {view.requiresSelection && role.selectable && onSelectRole ? (
                      <input
                        type="radio"
                        name="role-mod-v4-target"
                        checked={pickedRole === role.role}
                        onChange={() => onSelectRole(role.role)}
                        disabled={!canMutate || submitting}
                        aria-label={`Select ${role.role}`}
                        data-testid={`role-mod-v4-pick-${role.role}`}
                      />
                    ) : null}
                    <span className="font-mono">{role.role}</span>
                    {role.recommended ? <Badge>Recommended</Badge> : null}
                    {view.selectedRole === role.role ? <Badge>Selected</Badge> : null}
                    {!role.selectable && !view.selectedRole ? <Badge variant="outline">Not eligible</Badge> : null}
                  </div>
                  {role.description ? <p className="mt-1 text-muted-foreground">{role.description}</p> : null}
                  <RoleComparisonDetail
                    role={role}
                    defaultOpen={role.recommended && !view.selectedRole}
                    selected={view.selectedRole === role.role}
                  />
                </li>
              ))}
            </ul>
          ) : view.roles.length ? (
            <ul className="space-y-2">
              {view.roles.map((role) => (
                <li key={role.role} className="rounded-md border border-cp-line/60 p-3" data-testid={`role-mod-v4-role-${role.role}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono">{role.role}</span>
                    {view.selectedRole ? (
                      <Badge variant={role.selected ? 'default' : 'outline'}>{role.selected ? 'Selected' : 'Not selected'}</Badge>
                    ) : (
                      <Badge variant="outline">Role selection not reached</Badge>
                    )}
                  </div>
                  {role.description ? <p className="mt-1 text-muted-foreground">{role.description}</p> : null}
                  {role.whyNot ? <p className="mt-1">{role.whyNot}</p> : null}
                </li>
              ))}
            </ul>
          ) : null}
          {view.authChoice && pickedRole === view.authChoice.role ? (
            <div className="space-y-3" data-testid="role-mod-v4-auth-choice">
              <p>More than one authorization instance could receive a value. Choose the instance.</p>
              {view.authChoice.checks.map((check) => (
                <fieldset key={check.checkId} className="space-y-1">
                  <legend className="font-mono text-xs">{check.object}</legend>
                  {check.instances.map((instance) => (
                    <label key={`${instance.auth}-${instance.variant}`} className="flex items-center gap-2 font-mono text-xs">
                      <input
                        type="radio"
                        name={`auth-${check.checkId}`}
                        checked={authChoices[check.checkId]?.auth === instance.auth && (authChoices[check.checkId]?.variant || '') === (instance.variant || '')}
                        onChange={() => setAuthChoices((current) => ({
                          ...current,
                          [check.checkId]: { check_id: check.checkId, auth: instance.auth, variant: instance.variant || '' },
                        }))}
                      />
                      AUTH {instance.auth || '—'} · variant {instance.variant || '—'}
                    </label>
                  ))}
                </fieldset>
              ))}
            </div>
          ) : null}
          {view.requiresSelection && onConfirmSelection ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-start gap-3">
                <Button
                  type="button"
                  onClick={confirm}
                  disabled={!canMutate || submitting || !pickedRole}
                  data-testid="role-mod-v4-confirm-role"
                >
                  {submitting ? 'Confirming the role…' : 'Confirm role'}
                </Button>
                {onDeclineRole ? (
                  <DeclineEscalation
                    testId="role-mod-v4-decline-role"
                    disabled={!canMutate}
                    submitting={submitting}
                    onDecline={() => onDeclineRole({ role: pickedRole })}
                  />
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                Confirm role is the approval. HALO re-reads the role from SAP, rebuilds the exact change, and applies it only if nothing changed since this comparison. No role is created. Decline and escalate leaves the comparison unchanged and does not write to SAP. Review notes are entered on the human escalation banner.
              </p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card data-testid="role-mod-v4-operations">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{view.finalReview ? 'Final change review' : 'Proposed Change'}</CardTitle>
          <CardDescription>
            {view.finalReview
              ? 'Rebuilt from a fresh read of the confirmed role. This exact list was sent to SAP as one grouped change.'
              : 'The stored operation list. Approval sends this list unchanged.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {view.finalReview ? (
            <div className="space-y-3" data-testid="role-mod-v4-final-review">
              <div className="grid gap-3 sm:grid-cols-3">
                <Fact label="Selected role" value={view.selectedRole} mono />
                <Fact
                  label="Users affected"
                  value={view.assignmentCount == null ? 'Assigned-user count was not available' : `${view.assignmentCount} assigned user${Number(view.assignmentCount) === 1 ? '' : 's'}${view.shared ? ' · shared role' : ''}`}
                />
                <Fact
                  label="Role state"
                  value={view.roleStateVerified || 'Role state verified. No changes detected since analysis.'}
                  testId="role-mod-v4-role-state"
                />
              </div>
              {view.snapshotHash ? (
                <Disclosure label="Technical details" openLabel="Hide technical details" testId="role-mod-v4-technical">
                  <p className="font-mono text-xs break-all">Snapshot hash {view.snapshotHash}</p>
                </Disclosure>
              ) : null}
            </div>
          ) : null}
          {view.operations.length ? (
            <ul className="space-y-3">
              {view.operations.map((operation) => (
                <li key={`${operation.object}-${operation.index}`} className="rounded-md border border-cp-line/70 p-3" data-testid={`role-mod-v4-operation-${operation.index}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm">{operation.object}</span>
                    <Badge variant="outline">{MODE_LABEL[operation.mode] || operation.mode}</Badge>
                  </div>
                  <p className="mt-1 text-sm">{describeAuthorizationChange(operation)}</p>
                  {operation.mode === 'add_to_instance' ? (
                    <p className="mt-1 font-mono text-xs">AUTH {operation.auth || '—'} · variant {operation.variant || '—'}</p>
                  ) : null}
                  <FieldList fields={operation.fields} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="role-mod-v4-empty">{view.emptyState}</p>
          )}
        </CardContent>
      </Card>

      <Card data-testid="role-mod-v4-safety">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Safety / Validation</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1.5 text-sm">
            {view.safety.map((check) => (
              <li key={check.key} className="flex items-start gap-2">
                {check.warn ? (
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-cp-crit" />
                ) : check.ok ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cp-ok" />
                ) : (
                  <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span>{check.label}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {view.apply ? (
        <Card data-testid="role-mod-v4-apply">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Apply Result</CardTitle>
            <CardDescription>{view.apply.headline}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Fact label="SAP status" value={view.apply.status} mono testId="role-mod-v4-sap-status" />
            <Fact label="Before hash" value={view.apply.beforeHash} mono />
            <Fact label="After hash" value={view.apply.afterHash} mono />
            <Fact label="Transport" value={view.apply.transportId} mono />
            <Fact label="Transport status" value={view.apply.transportStatus} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

export function RoleModRoleAnalysis({
  ticket,
  selectedRole,
  onSelectRole,
  onConfirmSelection,
  submitting,
  canMutate,
}) {
  if (isRoleModV4(ticket)) return null;
  const analysis = getRoleModRoleAnalysis(ticket);
  if (!analysis.rows.length) return null;
  const selectable = analysis.requiresSelection;
  return (
    <Card data-testid="role-mod-role-analysis">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5" />
          Role Analysis
        </CardTitle>
        <CardDescription>
          {analysis.scoring?.version === 'v3'
            ? 'Only roles already assigned to the user were considered. Role Fit ranks existing SAP authorization structure. It is not a probability. Impact does not change the score.'
            : 'Only roles already assigned to the user were considered. Role Fit Score ranks existing SAP authorization structure. It is not a probability. Impact does not change the score.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {selectable ? (
          <div className="rounded-lg border border-cp-mod/50 bg-cp-inset p-3 text-sm" data-testid="role-mod-selection-required">
            <p className="font-medium">
              {analysis.selectionCause === 'agent_context_contradiction'
                ? 'Human selection required'
                : analysis.selectionCause === 'supporting_counts'
                  ? 'These roles share the requested authorization structure.'
                  : 'The top role fit scores are within 5 points.'}
            </p>
            <p className="mt-1 text-muted-foreground">
              {analysis.selectionCause === 'agent_context_contradiction'
                ? `HALO found a structurally valid target${
                    analysis.agentContext?.structural_winner
                      ? ` (${analysis.agentContext.structural_winner})`
                      : ''
                  }, but the available business context conflicts with the automatic selection. ${
                    analysis.agentContext?.conflict?.role_evidence
                      ? `Role context: ${analysis.agentContext.conflict.role_evidence}. `
                      : ''
                  }${
                    analysis.agentContext?.conflict?.request_evidence
                      ? `Request context: ${analysis.agentContext.conflict.request_evidence}. `
                      : ''
                  }Choose which structurally eligible assigned role should receive the addition. Selecting does not modify SAP; approval remains a separate step.`
                : analysis.selectionCause === 'supporting_counts'
                  ? `${analysis.candidateRoles.join(', ')} differ by existing-value count or by other fields on the same object. Those counts stay in the Role Fit Score and do not choose the role. Choose which assigned role should receive the addition. Selecting does not modify SAP; approval remains a separate step.`
                  : `${analysis.candidateRoles.join(', ')} are close enough that assignment order is not used to choose one. Compare authorization structure and impact, then choose which assigned role should receive the addition. Selecting does not modify SAP; approval remains a separate step.`}
            </p>
          </div>
        ) : analysis.selectedRole ? (
          <div className="space-y-3 text-sm" data-testid="role-mod-selected-summary">
            <div data-testid="role-mod-functional-fit" className="space-y-2">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {analysis.scoring?.version === 'v3' || analysis.scoring?.version === 'v2'
                  ? 'Functional fit'
                  : 'Stored score'}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground">Selected role</span>
                <span className="font-mono font-semibold">{analysis.selectedRole}</span>
                {analysis.humanSelection ? (
                  <Badge variant="outline" className="text-[10px]">
                    Human selection
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px]">
                    Strongest role fit
                  </Badge>
                )}
              </div>
              {analysis.selectedScore != null ? (
                <div data-testid="role-mod-score-label">
                  <p>
                    {analysis.scoring.scoreLabel}:{' '}
                    <span className="font-mono">
                      {analysis.scoring?.version === 'v3' ? analysis.displayScore : analysis.selectedScore}
                      {analysis.scoring?.version === 'v3' ? ' / 100' : ''}
                    </span>
                  </p>
                  {analysis.scoring?.version === 'v3' ? null : (
                    <p data-testid="role-mod-scoring-model">Scoring model: {analysis.scoring.modelLabel}</p>
                  )}
                </div>
              ) : null}
              {analysis.scoring?.version === 'v3' && analysis.agentContext ? (
                <p data-testid="role-mod-agent-context">
                  Agent Context: {agentContextLabel(analysis.agentContext.status)}
                </p>
              ) : null}
              {analysis.whyBullets?.length ? (
                <div>
                  <p className="text-muted-foreground">Why</p>
                  <ul className="mt-1 list-disc pl-4">
                    {analysis.whyBullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
            <div data-testid="role-mod-impact" className="space-y-1">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Impact</p>
              <ul className="list-disc pl-4 text-muted-foreground">
                {analysis.impact?.assignedUserCount != null ? (
                  <li>
                    {analysis.impact.assignedUserCount} assigned user
                    {analysis.impact.assignedUserCount === 1 ? '' : 's'}
                  </li>
                ) : null}
                {analysis.impact?.shared != null ? (
                  <li>{analysis.impact.shared ? 'Shared role' : 'Not shared'}</li>
                ) : null}
                <li>{analysis.impact?.lockLabel || 'Not yet read'}</li>
                {analysis.impact?.delta ? <li>Proposed delta: {analysis.impact.delta}</li> : null}
              </ul>
            </div>
            <p className="text-muted-foreground">
              {analysis.otherEvaluated} other assigned role{analysis.otherEvaluated === 1 ? '' : 's'} evaluated
              {otherAcceptedPhrase(analysis)}.
            </p>
          </div>
        ) : null}

        <Disclosure
          label="View detailed analysis"
          openLabel="Hide detailed analysis"
          defaultOpen={selectable}
          testId="role-mod-detailed-analysis"
        >
          <ul className="space-y-3 text-sm">
            {analysis.rows.map((row) => (
              <RoleDetailRow
                key={row.role}
                row={row}
                scoring={analysis.scoring}
                selectable={selectable}
                selectedRole={selectedRole}
                onSelectRole={onSelectRole}
              />
            ))}
          </ul>
        </Disclosure>

        {selectable ? (
          <span title={permissionDeniedTitle(canMutate, 'select the target role')}>
            <Button
              onClick={onConfirmSelection}
              disabled={!selectedRole || submitting || !canMutate}
              data-testid="role-mod-confirm-selection"
            >
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Continue with selected role
            </Button>
          </span>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SafetyCheckList({ ticket }) {
  if (isRoleModV4(ticket)) return null;
  const checks = getRoleModSafetyChecks(ticket);
  return (
    <ul className="space-y-1.5 text-sm" data-testid="role-mod-safety-checks">
      {checks.map((check) => (
        <li key={check.key} className="flex items-start gap-2">
          {check.warn ? (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-cp-crit" />
          ) : check.ok ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cp-ok" />
          ) : check.pending ? (
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <span className={check.warn ? 'text-cp-crit' : ''}>{check.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** Approval card. Request, change, and the growing summary live here once. */
export function RoleModActionRequest({
  ticket,
  decision,
  onApprove,
  onReject,
  submitting,
  canMutate,
  analysisRunning = false,
  gateSpec = null,
  gateDescription = '',
  onGateDescriptionChange,
  gateEvidence = null,
  onGateEvidenceChange,
  onGateAction,
}) {
  const stage = ticket?.workflow_stage;
  const failure = getRoleModFailureContext(ticket);
  const request = getRoleModOriginalRequest(ticket);
  const approval = getApproval(ticket);
  const transport = getRoleModApprovalTransport(ticket);
  const summary = buildRoleModHaloSummary(ticket);
  const closure = getRoleModClosure(ticket);
  const state = getWorkflowState(ticket);
  const fiori = state.workflow_agent === 'fiori_catalog' || Number(state.fiori_workflow_version || 0) >= 2;
  const catalog = fiori ? state.menu_change?.proposal || {} : {};
  const followUp = fiori ? state.catalog_role_follow_up || {} : {};
  const handoff = followUp.handoff || {};
  const profile = followUp.final_profile_execution || followUp.profile_execution || followUp.initial_profile_execution || {};
  const cleanup = followUp.correction_execution || {};
  const trace = followUp.trace_upload || {};
  const fioriRequest = String(request.description || '').split(/\n\s*--- Information added by/i)[0].trim();
  const cleanupValues = Array.isArray(handoff.cleanup)
    ? handoff.cleanup.map((row) => [row.object, row.field, row.low].filter(Boolean).join(' ')).join(', ')
    : '';
  const fioriChecks = fiori
    ? [
        state.menu_change?.lifecycle === 'APPLIED' ? 'Catalog change applied and verified in SAP' : '',
        followUp.stages?.some((row) => row.key === 'authorization_merge' && row.status === 'done')
          ? 'PFCG refresh and SU24 authorization preparation completed'
          : '',
        cleanup.verified === true
          ? `Approved authorization cleanup verified${cleanupValues ? `: removed ${cleanupValues}` : ''}`
          : '',
        profile.verified === true || handoff.profile_name
          ? `Profile ${profile.profile_name || handoff.profile_name} verified in AGR_1016`
          : '',
        followUp.transport_verification?.verified === true
          ? `Transport ${followUp.transport_verification.transport_id || handoff.transport_id} role objects verified`
          : '',
      ].filter(Boolean)
    : [];
  const pending = stage === WORKFLOW_STAGE.PENDING_APPROVAL;
  const v4 = isRoleModV4(ticket);
  const v4Reason = v4
    ? roleModV4ReasonText(getWorkflowState(ticket).human_reason_code || getWorkflowState(ticket).failure_code || getWorkflowState(ticket).apply_status)
    : '';
  const stageLabel = closure
    ? 'closed — access already granted'
    : v4Reason
      ? v4Reason
      : pending
        ? 'Awaiting approval'
        : String(stage || '').replaceAll('_', ' ').toLowerCase();
  // For V4, Confirm role is the action point. The card is titled for what
  // happened, not for a second approval that never occurs.
  const confirmRole = v4 && String(approval.mode || '') === 'confirm_role';
  const confirmedChangeApplied = confirmRole
    && approval.status === 'approved'
    && ['APPLIED', 'ALREADY_PRESENT', 'APPLIED_TRANSPORT_FAILED'].includes(
      String(getWorkflowState(ticket).sap_apply_status || getWorkflowState(ticket).apply_status || '').toUpperCase(),
    );
  const title = closure ? 'Outcome' : confirmRole ? 'Role confirmation' : 'Approval';
  return (
    <Card className={closure ? 'border-cp-ok/40' : 'border-cp-indigo/40'} data-testid="role-mod-action-request">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          {closure ? <ShieldCheck className="h-5 w-5" /> : <UserCheck className="h-5 w-5" />}
          {title}
        </CardTitle>
        <CardDescription>
          {fiori
            ? 'Fiori catalog, role authorization, profile, and transport facts relevant to this manual governance decision.'
            : closure
            ? 'HALO closed this ticket. No approval was needed because the user already holds the authorization.'
            : pending
              ? 'Review the request and the exact addition. Approval changes role content. It does not assign a role or release a transport.'
              : 'The summary below is updated as each workflow step is recorded.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div data-testid="role-mod-original-request">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Request</p>
          <p className="mt-1 text-sm whitespace-pre-wrap" data-testid="role-mod-request-description">
            {(fiori ? fioriRequest : request.description) || <span className="text-muted-foreground">Not provided</span>}
          </p>
        </div>
        {fiori ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="fiori-governance-facts">
            <Fact
              label="Application"
              value={[catalog.source_configuration?.title || catalog.application || handoff.fiori_id, catalog.application || handoff.fiori_id]
                .filter((value, index, values) => value && values.indexOf(value) === index)
                .join(' · ')}
              mono
            />
            <Fact label="Catalog" value={catalog.catalog_id || followUp.catalog_id || handoff.catalog_id} mono />
            <Fact label="Target role" value={catalog.role || followUp.role || handoff.role} mono />
            <Fact label="System / client" value={[catalog.target_system, catalog.target_client].filter(Boolean).join(' / ')} mono />
            <Fact label="Generated profile" value={profile.profile_name || handoff.profile_name} mono />
            <Fact label="Transport request" value={handoff.transport_id || followUp.transport_verification?.transport_id} mono />
            {cleanupValues ? <Fact label="Approved cleanup" value={`Removed ${cleanupValues}`} mono /> : null}
            {trace.filename ? <Fact label="Authorization trace" value={`${trace.filename} · ${trace.check_count || 0} checks`} mono /> : null}
            <Fact label="Current workflow status" value={stageLabel} />
          </div>
        ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Fact label="User" value={decision?.user || request.user} mono />
          <Fact label="Instance" value={decision?.instance || decision?.systemClient || request.instance || request.systemClient} mono />
          {v4 ? null : <Fact label="Failed transaction / action" value={failure.failedActionLabel} mono={Boolean(failure.failedAction)} />}
          {v4 ? null : <Fact label="Authorization evidence" value={failure.authorizationFailure} mono />}
          <Fact label="Target existing role" value={decision?.targetRole} mono />
          <Fact label="Users affected" value={decision?.affectedUsers} mono />
          {v4 ? null : <Fact label="Requested addition" value={decision?.proposedAddition} mono />}
          <Fact
            label="Evidence file"
            value={<EvidenceChip artifact={failure.evidenceArtifact || (request.evidenceFilename ? { filename: request.evidenceFilename } : null)} sourceLabel={failure.evidenceSource} />}
          />
          <Fact label="Current workflow status" value={stageLabel} />
        </div>
        )}

        {!fiori && (transport.id || transport.full) ? (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="role-mod-approval-transport">
            <Fact label="Transport request" value={transport.id} mono />
            <Fact label="Full description" value={transport.full} />
          </div>
        ) : null}

        {!fiori && transport.roleChangeDescription ? (
          <Fact
            label="Role change description"
            value={<span className="whitespace-pre-wrap">{transport.roleChangeDescription}</span>}
            testId="role-mod-role-change-description"
          />
        ) : null}

        <div className="rounded-lg border border-cp-line/70 p-3">
          <p className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            {fiori ? 'Verified workflow facts' : 'Safety checks'}
          </p>
          {fiori ? (
            <ul className="space-y-2 text-sm" data-testid="fiori-governance-checks">
              {fioriChecks.map((label) => (
                <li key={label} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cp-ok" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          ) : <SafetyCheckList ticket={ticket} />}
        </div>

        {closure ? (
          <div
            className="flex items-start gap-2 rounded-lg border border-cp-ok p-3 text-cp-ok"
            data-testid="role-mod-closure"
          >
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">
                Closed — access already granted by {closure.roles.join(', ') || 'an assigned role'}
                {closure.at ? ` • ${formatWhen(closure.at)}` : ''}
              </p>
              {closure.summary ? <p>{closure.summary}</p> : null}
              <p className="text-muted-foreground">
                No role change, approval, or transport was made. SAP is unchanged.
              </p>
            </div>
          </div>
        ) : null}

        {!fiori && summary.length ? (
          <pre
            className="whitespace-pre-wrap text-sm bg-muted rounded-lg p-4 max-h-72 overflow-auto font-sans"
            data-testid="role-mod-halo-summary"
          >
            {summary.join('\n\n')}
          </pre>
        ) : null}

        {gateSpec ? (
          <RoleModGateForm
            embedded
            spec={gateSpec}
            ticket={ticket}
            description={gateDescription}
            onDescriptionChange={onGateDescriptionChange}
            evidence={gateEvidence}
            onEvidenceChange={onGateEvidenceChange}
            onAction={onGateAction}
            submitting={submitting}
            canMutate={canMutate}
          />
        ) : null}

        {pending ? (
          <div className="flex flex-wrap items-center gap-3">
            <span title={permissionDeniedTitle(canMutate, 'approve this request')}>
              <Button
                onClick={onApprove}
                disabled={submitting || !canMutate}
                className="bg-cp-ok hover:bg-cp-ok"
                data-testid="workflow-approve-btn"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Approve exact role change
              </Button>
            </span>
            <span title={permissionDeniedTitle(canMutate, 'reject this request')}>
              <Button
                variant="outline"
                onClick={onReject}
                disabled={submitting || !canMutate}
                className="border-cp-crit text-cp-crit hover:bg-cp-inset"
                data-testid="workflow-reject-btn"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </span>
          </div>
        ) : analysisRunning ? (
          <div className="flex items-center gap-2 rounded-lg border border-muted bg-muted/40 p-3 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin shrink-0" />
            <span className="text-sm font-medium">Waiting for analysis to finish before approval is available.</span>
          </div>
        ) : confirmRole ? (
          // The confirming reviewer is listed once, under Human decisions.
          <div
            className={`flex items-center gap-2 rounded-lg border p-3 ${
              confirmedChangeApplied ? 'border-cp-ok text-cp-ok' : 'border-cp-crit text-cp-crit'
            }`}
            data-testid="role-mod-confirm-role-note"
          >
            {confirmedChangeApplied ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
            <span className="text-sm font-medium">
              {confirmedChangeApplied
                ? 'Role confirmed. Change applied. The decision record is under Human decisions.'
                : 'Role confirmed. The change was not applied. The decision record is under Human decisions.'}
            </span>
          </div>
        ) : approval.status === 'approved' || approval.status === 'rejected' ? (
          <div
            className={`flex items-center gap-2 rounded-lg border p-3 ${
              approval.status === 'approved' ? 'border-cp-ok text-cp-ok' : 'border-cp-crit text-cp-crit'
            }`}
          >
            {approval.status === 'approved' ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
            <span className="text-sm font-medium">
              {approval.status === 'approved' ? 'Approved' : 'Rejected'}
              {approval.by ? ` by ${approval.by}` : ''}
              {approval.at ? ` • ${formatWhen(approval.at)}` : ''}
            </span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Human-readable failure first; the technical reason underneath. */
export function RoleModFailureCard({ ticket }) {
  const state = getWorkflowState(ticket);
  const result = state.mcp_result && typeof state.mcp_result === 'object' ? state.mcp_result : {};
  const human = humanizeRoleModError(result.error || state.error, result.detail);
  return (
    <Card className="border-cp-crit" data-testid="role-mod-failure">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2 text-cp-crit">
          <ShieldAlert className="h-5 w-5" />
          Role change did not complete
        </CardTitle>
        <CardDescription>{human.headline}</CardDescription>
      </CardHeader>
      <CardContent>
        {human.technical ? (
          <Disclosure label="Technical details" openLabel="Hide technical details" testId="role-mod-failure-technical">
            <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 text-xs font-mono">
              {human.technical}
              {result.provenance?.destination ? `\nSAP: ${result.provenance.destination}` : ''}
            </pre>
          </Disclosure>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Completed milestone. There is no release action. */
export function RoleModTransportCreated({ ticket }) {
  const state = getWorkflowState(ticket);
  const record = state.transport_created && typeof state.transport_created === 'object' ? state.transport_created : null;
  const transportId = record?.transport_id || state.transport_id || state.mcp_result?.transport_id;
  const transportOnly = String(state.sap_apply_status || state.apply_status || '').toUpperCase() === 'APPLIED_TRANSPORT_FAILED';
  if (!record && !transportId && !transportOnly) return null;
  if (!record && !['DONE', 'AWAITING_QA_FUT', 'AWAITING_CAB', 'AWAITING_PROD_CONFIRMATION', 'QA_FUT_FAILED', 'CAB_REJECTED', 'FUT_REMEDIATION_REQUIRED'].includes(ticket?.workflow_stage) && state.current_step !== 'qa_fut') {
    return null;
  }
  return (
    <Card data-testid="role-mod-transport-created">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{transportOnly ? 'Transport recovery' : 'Transport Created'}</CardTitle>
        <CardDescription>
          {transportOnly
            ? 'The role change succeeded. Only transport recovery remains. HALO did not apply the role change again.'
            : 'HALO created the transport and stopped. Release and import are handled outside HALO.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2">
        <Fact label="Transport" value={transportId} mono testId="role-mod-transport-id" />
        <Fact label="SAP description" value={record?.actual_sap_transport_description || state.transport_description_sap} />
        <Fact label="Full change description" value={record?.full_change_description || state.transport_description_full} />
        <Fact label="Role change" value="Applied" testId="role-mod-role-change-status" />
        <Fact label="Transport Request" value={transportOnly ? 'Recovery remains' : 'Created'} testId="role-mod-transport-request-status" />
        <Fact label="Created" value={record?.created_at ? formatWhen(record.created_at) : null} />
        <Fact label="Downstream" value={record?.note || 'Downstream release and import are handled outside HALO. HALO did not release or import this transport.'} />
      </CardContent>
    </Card>
  );
}

/** Before-submission form for the gate the ticket is currently waiting on. */
export function RoleModGateForm({
  spec,
  ticket,
  description,
  onDescriptionChange,
  evidence,
  onEvidenceChange,
  onAction,
  submitting,
  canMutate,
  embedded = false,
}) {
  const fileInputRef = useRef(null);
  const gate = spec?.gate || null;
  // A new gate starts with an empty picker so the previous step's file is never re-shown.
  useEffect(() => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (onEvidenceChange) onEvidenceChange(null);
  }, [gate, onEvidenceChange]);
  // Clearing the selection (after submit or via Remove) also clears the native input.
  useEffect(() => {
    if (!evidence && fileInputRef.current) fileInputRef.current.value = '';
  }, [evidence]);
  if (!spec) return null;
  const state = getWorkflowState(ticket);
  const transportId = state.transport_id || state.mcp_result?.transport_id;
  const needsText = spec.descriptionRequired && !String(description || '').trim();
  const needsFile = spec.evidenceRequired && !evidence;
  const secondaryNeedsText = (spec.secondaryDescriptionRequired ?? spec.descriptionRequired)
    && !String(description || '').trim();
  const secondaryNeedsFile = (spec.secondaryEvidenceRequired ?? spec.evidenceRequired) && !evidence;
  const evidenceLabel = spec.evidenceLabel || 'Evidence attachment';
  const removeEvidence = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    onEvidenceChange(null);
  };
  const fields = (
    <>
        <div className="rounded-lg border border-cp-line/70 p-3 text-sm">
          <p><span className="text-muted-foreground">HALO needs: </span>{spec.needs}</p>
          {spec.optional ? <p className="mt-1"><span className="text-muted-foreground">Optional: </span>{spec.optional}</p> : null}
          {spec.after ? <p className="mt-1"><span className="text-muted-foreground">After submission: </span>{spec.after}</p> : null}
        </div>
        {transportId ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Fact label="Transport" value={transportId} mono />
            <Fact label="SAP description" value={state.transport_description_sap} />
          </div>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="role-mod-stage-description">{spec.responseLabel}</Label>
          <Textarea
            id="role-mod-stage-description"
            value={description}
            onChange={(event) => onDescriptionChange(event.target.value)}
            placeholder="Your response is persisted on the ticket and shown after submission."
            disabled={!canMutate || submitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="role-mod-stage-evidence">
            {evidenceLabel}
            {spec.evidenceRequired ? ' (required)' : ' (optional)'}
          </Label>
          <Input
            id="role-mod-stage-evidence"
            ref={fileInputRef}
            type="file"
            accept={spec.evidenceAccept || 'image/png,image/jpeg,image/webp,application/pdf'}
            onChange={(event) => onEvidenceChange(event.target.files?.[0] || null)}
            disabled={!canMutate || submitting}
          />
          {evidence ? (
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="role-mod-selected-evidence">
              <FileImage className="h-3.5 w-3.5 text-cp-indigo" />
              <span className="font-mono">{evidence.name}</span>
              <button
                type="button"
                className="text-cp-crit hover:underline"
                onClick={removeEvidence}
                disabled={submitting}
                data-testid="role-mod-remove-evidence"
              >
                Remove
              </button>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-3">
          <span title={permissionDeniedTitle(canMutate, 'record this workflow action')}>
            <Button
              onClick={() => onAction(spec.primary[0])}
              disabled={submitting || !canMutate || needsText || needsFile}
              className="bg-cp-ok hover:bg-cp-ok"
              data-testid="role-mod-gate-primary"
            >
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {spec.primary[1]}
            </Button>
          </span>
          {spec.secondary ? (
          <span title={permissionDeniedTitle(canMutate, 'record this workflow action')}>
            <Button
              variant="outline"
              onClick={() => onAction(spec.secondary[0])}
              disabled={submitting || !canMutate || secondaryNeedsText || secondaryNeedsFile}
              className="border-cp-crit text-cp-crit"
              data-testid="role-mod-gate-secondary"
            >
              {spec.secondary[1]}
            </Button>
          </span>
          ) : null}
        </div>
    </>
  );
  if (embedded) {
    return (
      <div className="space-y-4" data-testid="role-mod-gate-form">
        <div>
          <p className="text-sm font-medium">{spec.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{spec.request}</p>
        </div>
        {fields}
      </div>
    );
  }
  return (
    <Card className="border-cp-indigo/40" data-testid="role-mod-gate-form">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <UserCheck className="h-5 w-5" />
          {spec.title}
        </CardTitle>
        <CardDescription>{spec.request}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">{fields}</CardContent>
    </Card>
  );
}

/** Every recorded human decision, with the submitted response kept visible. */
export function RoleModDecisionHistory({ ticket }) {
  const records = getRoleModGateRecords(ticket);
  // Collapsed by default once a decision has been recorded; the header still
  // shows how many decisions are on the ticket.
  const [open, setOpen] = useState(false);
  if (!records.length) return null;
  const latest = records[records.length - 1];
  return (
    <Card data-testid="role-mod-decision-history">
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center gap-2 text-left"
          data-testid="role-mod-decision-history-toggle"
        >
          {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <ClipboardList className="h-5 w-5" />
          <CardTitle className="text-lg">Human decisions</CardTitle>
          <Badge variant="outline">{records.length}</Badge>
        </button>
        <CardDescription>
          {open
            ? 'Every human decision recorded on this ticket: who decided, what, and when.'
            : `Latest: ${latest.title} — ${latest.decisionLabel}${latest.actor ? ` by ${latest.actor}` : ''}${latest.at ? ` • ${formatWhen(latest.at)}` : ''}`}
        </CardDescription>
      </CardHeader>
      {open ? (
      <CardContent className="space-y-3">
        {records.map((record) => (
          <div
            key={record.key}
            className={`rounded-lg border p-3 ${
              record.positive ? 'border-cp-ok/50' : 'border-cp-crit/50'
            }`}
            data-testid={`role-mod-gate-record-${record.key}`}
          >
            <div className="flex flex-wrap items-center gap-2">
              {record.positive ? (
                <CheckCircle2 className="h-4 w-4 text-cp-ok" />
              ) : (
                <XCircle className="h-4 w-4 text-cp-crit" />
              )}
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{record.title}</span>
              <Badge className={record.positive ? 'bg-cp-ok text-white' : 'bg-cp-crit text-white'}>
                {record.decisionLabel}
              </Badge>
            </div>
            <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
              <p>
                <span className="text-muted-foreground">
                  {record.actorLabel || (record.positive ? 'Approved by' : 'Recorded by')}:{' '}
                </span>
                {record.actor || 'Not recorded'}
              </p>
              <p>
                <span className="text-muted-foreground">At: </span>
                {record.at ? formatWhen(record.at) : 'Not recorded'}
              </p>
            </div>
            {record.details?.length ? (
              <ul className="mt-2 space-y-1 text-sm" data-testid={`role-mod-gate-details-${record.key}`}>
                {record.details.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : null}
            {record.response ? (
              <div className="mt-2 text-sm">
                <p className="text-muted-foreground">{record.responseLabel}:</p>
                <p className="mt-1 whitespace-pre-wrap" data-testid={`role-mod-gate-response-${record.key}`}>
                  {`“${record.response}”`}
                </p>
              </div>
            ) : null}
            {record.evidence?.filename ? (
              <div className="mt-2 text-sm">
                <span className="text-muted-foreground">Evidence: </span>
                <EvidenceChip
                  artifact={record.evidence}
                  step={record.title}
                  sourceLabel={evidenceDisplayLabel(record.evidence?.source || record.evidence?.type, record.evidence?.filename)}
                />
                {record.evidenceActor ? (
                  <span className="text-muted-foreground"> · uploaded by {record.evidenceActor}</span>
                ) : null}
              </div>
            ) : null}
            <p className="mt-2 text-xs text-muted-foreground">
              {[record.executionMode ? `Mode: ${record.executionMode.replaceAll('_', ' ')}` : '', record.note, record.nextStep]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
        ))}
      </CardContent>
      ) : null}
    </Card>
  );
}

/** Customer files and comments, split under Ticket / FUT / CAB / Prod headings. */
export function RoleModDocuments({ ticket }) {
  const groups = getRoleModDocumentGroups(ticket);
  if (!groups.length) return null;
  return (
    <Card data-testid="role-mod-documents">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileImage className="h-5 w-5" />
          Documents
        </CardTitle>
        <CardDescription>
          Screenshots, documents, and the comments entered with them, kept on the ticket for later review.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {groups.map((group) => (
          <section key={group.key} data-testid={`role-mod-document-group-${group.key}`}>
            <h4 className="text-sm font-semibold">{group.title}</h4>
            <p className="text-xs text-muted-foreground">{group.description}</p>
            <div className="mt-2 space-y-3">
              {group.documents.map((doc) => (
                <div
                  key={`${doc.group}:${doc.id}`}
                  className="rounded-lg border border-cp-line/70 p-3"
                  data-testid="role-mod-document"
                >
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{doc.step}</p>
                  {doc.comment ? (
                    <p className="mt-2 text-sm whitespace-pre-wrap">
                      <span className="text-muted-foreground">Comment: </span>“{doc.comment}”
                    </p>
                  ) : null}
                  {doc.actor || doc.at ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {[doc.actor, doc.at ? formatWhen(doc.at) : ''].filter(Boolean).join(' · ')}
                    </p>
                  ) : null}
                  <div className="mt-2">
                    <EvidenceFile doc={doc} thumbnail />
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </CardContent>
    </Card>
  );
}
